'use client';

import { useHomeQuery } from '@/queries/home.query';
import clsx from 'clsx';
import { useInView } from 'framer-motion';
import { useEffect, useMemo, useRef, useState } from 'react';
import WhyChooseUs from './WhyChooseUs';

// Star Icon Component
function StarIcon(props) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" {...props}>
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

// Star Rating Component
function StarRating({ rating }) {
  return (
    <div className="flex">
      {[...Array(5)].map((_, index) => (
        <StarIcon
          key={index}
          className={clsx('h-5 w-5', rating > index ? 'fill-accent' : 'fill-gray-300')}
        />
      ))}
    </div>
  );
}

// Single Review Component
function Review({
  title,
  description,
  author_name,
  author_cm,
  author_avatar,
  rating,
  className,
  ...props
}) {
  const animationDelay = useMemo(() => {
    const delays = ['0s', '0.1s', '0.2s', '0.3s', '0.4s', '0.5s'];
    return delays[Math.floor(Math.random() * delays.length)];
  }, []);

  return (
    <figure
      className={clsx(
        'animate-fade-in rounded-2xl bg-white p-6 shadow-lg ring-1 ring-gray-900/5',
        className
      )}
      style={{ animationDelay }}
      {...props}
    >
      <blockquote className="text-gray-900 pb-2">
        <StarRating rating={rating} />
        <p className="mt-4 text-sm font-semibold leading-6 before:content-['“'] after:content-['”']">
          {title}
        </p>
        <p className="mt-3 text-xs leading-5">{description}</p>
      </blockquote>
      <figcaption className="flex flex-wrap items-center gap-x-4 gap-y-4 border-t border-gray-900/10 py-4 sm:flex-nowrap">
        <img
          alt={author_name}
          src={author_avatar}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          className="h-8 w-8 flex-none rounded-full bg-gray-50"
          loading="lazy"
        />
        <div className="flex-auto">
          <div className="text-xs font-semibold">{author_name}</div>
          <div className="text-xs text-gray-600">{`@${author_cm}`}</div>
        </div>
      </figcaption>
    </figure>
  );
}

// Split array utility
function splitArray(array, numParts) {
  const result = [];
  for (let i = 0; i < array.length; i++) {
    const index = i % numParts;
    if (!result[index]) result[index] = [];
    result[index].push(array[i]);
  }
  return result;
}

// Column with marquee scroll
function ReviewColumn({ reviews, className, reviewClassName, msPerPixel = 0 }) {
  const columnRef = useRef(null);
  const [columnHeight, setColumnHeight] = useState(0);
  const duration = `${columnHeight * msPerPixel}ms`;

  useEffect(() => {
    if (!columnRef.current) return;
    const resizeObserver = new window.ResizeObserver(() => {
      setColumnHeight(columnRef.current?.offsetHeight ?? 0);
    });
    resizeObserver.observe(columnRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      ref={columnRef}
      className={clsx('animate-marquee space-y-8 py-4', className)}
      style={{ '--marquee-duration': duration }}
    >
      {reviews.concat(reviews).map((review, idx) => (
        <Review
          key={idx}
          aria-hidden={idx >= reviews.length}
          className={reviewClassName?.(idx % reviews.length)}
          {...review}
        />
      ))}
    </div>
  );
}

// Grid component
function ReviewGrid({ data }) {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, amount: 0.4 });
  const columns = splitArray(data, 3);
  const column1 = columns[0] || [];
  const column2 = columns[1] || [];
  const column3Parts = splitArray(columns[2] || [], 2);
  const column3Part1 = column3Parts[0] || [];
  const column3Part2 = column3Parts[1] || [];

  return (
    <div
      ref={containerRef}
      className="relative -mx-4 grid min-h-[100vh] xl:h-[45rem] max-h-[80vh] lg:min-h-[100vh] xl:min-h-[70vh] xl:max-h-[100vh] px-5 grid-cols-1 items-start gap-6 overflow-hidden lg:px-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3"
    >
      {isInView && (
        <>
          <ReviewColumn
            reviews={[...column1, ...column3Part1, ...column2]}
            reviewClassName={(idx) =>
              clsx(
                idx >= column1.length + column3Part1.length && 'md:hidden',
                idx >= column1.length && 'lg:hidden'
              )
            }
            msPerPixel={10}
          />
          {column2.length > 0 && (
            <ReviewColumn
              reviews={[...column2, ...column3Part2]}
              className="hidden md:block"
              reviewClassName={(idx) => (idx >= column2.length ? 'lg:hidden' : '')}
              msPerPixel={20}
            />
          )}
          {column3Part1.length + column3Part2.length > 0 && (
            <ReviewColumn
              reviews={[...column3Part1, ...column3Part2]}
              className="hidden lg:block"
              msPerPixel={15}
            />
          )}
        </>
      )}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-52 bg-gradient-to-b from-primary-50" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-52 bg-gradient-to-t from-primary-50" />
    </div>
  );
}

// Main Reviews component with API fetch
export function Reviews() {
  const { data, isLoading, isError } = useHomeQuery();

  const testimonials =
    data?.data?.testimonials?.map((item) => ({
      title: item.title,
      description: item.description,
      rating: item.rating,
      author_name: item.author_name,
      author_cm: item.author_cm,
      author_avatar: item.author_avatar,
    })) ?? [];

  if (isLoading || !testimonials.length || isError) return null;

  return (
    <section className="bg-primary-50 pt-10 md:pt-28">
      <div className="mx-auto px-5 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="relative order-2 lg:order-1">
          <ReviewGrid data={testimonials} />
        </div>
        <div className="relative order-1 lg:order-2 lg:pl-10 mx-auto lg:mx-0">
          <WhyChooseUs />
        </div>
      </div>
    </section>
  );
}
